import React from 'react';
import { FormFieldType, FormField, FormFieldOption } from '../../models/forms/form-models';

interface DynamicFormFieldProps {
  field: FormField;
  value: any;
  error?: string;
  onChange: (name: string, value: any) => void;
  disabled?: boolean;
  isRTL?: boolean;
}

export const DynamicFormField: React.FC<DynamicFormFieldProps> = ({
  field,
  value,
  error,
  onChange,
  disabled = false,
  isRTL = false
}) => {
  const baseClasses = "w-full p-4 border-2 rounded-lg focus:outline-none transition-colors text-gray-800";
  const borderClasses = error
    ? "border-red-300 focus:border-red-500"
    : "border-gray-200 focus:border-black";
  const disabledClasses = disabled ? "bg-gray-100 cursor-not-allowed" : "";

  const inputClasses = `${baseClasses} ${borderClasses} ${disabledClasses}`;

  const renderField = () => {
    switch (field.type) {
      case FormFieldType.TEXT:
      case FormFieldType.EMAIL:
      case FormFieldType.PHONE:
        return (
          <input
            type={field.type === FormFieldType.EMAIL ? 'email' : field.type === FormFieldType.PHONE ? 'tel' : 'text'}
            id={field.id}
            name={field.name}
            value={value || ''}
            onChange={(e) => onChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            required={field.required}
            className={inputClasses}
          />
        );

      case FormFieldType.NUMBER:
        return (
          <input
            type="number"
            id={field.id}
            name={field.name}
            value={value || ''}
            onChange={(e) => onChange(field.name, Number(e.target.value))}
            placeholder={field.placeholder}
            disabled={disabled}
            required={field.required}
            className={inputClasses}
          />
        );

      case FormFieldType.TEXTAREA:
        return (
          <textarea
            id={field.id}
            name={field.name}
            value={value || ''}
            onChange={(e) => onChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            required={field.required}
            rows={4}
            className={`${inputClasses} resize-none`}
          />
        );

      case FormFieldType.SELECT:
        return (
          <select
            id={field.id}
            name={field.name}
            value={value || ''}
            onChange={(e) => onChange(field.name, e.target.value)}
            disabled={disabled}
            required={field.required}
            className={`${inputClasses} bg-white`}
          >
            {field.options?.map((option: FormFieldOption) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case FormFieldType.RADIO:
        return (
          <div className="space-y-3">
            {field.options?.map((option: FormFieldOption) => (
              <label
                key={option.value}
                className={`flex items-start ${isRTL ? 'space-x-reverse' : ''} space-x-3 cursor-pointer`}
              >
                <input
                  type="radio"
                  name={field.name}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => onChange(field.name, e.target.value)}
                  disabled={disabled || option.disabled}
                  required={field.required}
                  className="mt-1 w-4 h-4 text-black border-2 border-gray-300 rounded focus:ring-black"
                />
                <span className="text-sm text-gray-700 leading-relaxed">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        );

      case FormFieldType.CHECKBOX:
        return (
          <label className={`flex items-start ${isRTL ? 'space-x-reverse' : ''} space-x-3 cursor-pointer`}>
            <input
              type="checkbox"
              id={field.id}
              name={field.name}
              checked={value || false}
              onChange={(e) => onChange(field.name, e.target.checked)}
              disabled={disabled}
              required={field.required}
              className="mt-1 w-5 h-5 text-black border-2 border-gray-300 rounded focus:ring-black"
            />
            <span className="text-sm text-gray-700 leading-relaxed">
              {field.label}
            </span>
          </label>
        );

      case FormFieldType.MULTI_SELECT:
        const currentValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
            {field.options?.map((option: FormFieldOption) => (
              <label
                key={option.value}
                className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-3 cursor-pointer py-1`}
              >
                <input
                  type="checkbox"
                  value={option.value}
                  checked={currentValues.includes(option.value)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...currentValues, option.value]
                      : currentValues.filter(v => v !== option.value);
                    onChange(field.name, newValues);
                  }}
                  disabled={disabled || option.disabled}
                  className="w-4 h-4 text-black border border-gray-300 rounded focus:ring-black"
                />
                <span className="text-sm text-gray-700">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        );

      case FormFieldType.DATE:
        return (
          <input
            type="date"
            id={field.id}
            name={field.name}
            value={value || ''}
            onChange={(e) => onChange(field.name, e.target.value)}
            disabled={disabled}
            required={field.required}
            className={inputClasses}
          />
        );

      case FormFieldType.FILE:
        return (
          <input
            type="file"
            id={field.id}
            name={field.name}
            onChange={(e) => onChange(field.name, e.target.files?.[0] || null)}
            disabled={disabled}
            required={field.required}
            className={`${baseClasses} ${disabledClasses} file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-black file:text-white hover:file:bg-gray-800`}
          />
        );

      default:
        return <div>Unsupported field type: {field.type}</div>;
    }
  };

  // Don't render label for checkbox as it's handled within the field
  const shouldRenderLabel = field.type !== FormFieldType.CHECKBOX;

  return (
    <div className="space-y-2">
      {shouldRenderLabel && (
        <label htmlFor={field.id} className="block text-sm font-semibold text-gray-800">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {renderField()}

      {field.helperText && (
        <p className="text-xs text-gray-500 leading-relaxed">
          {field.helperText}
        </p>
      )}

      {error && (
        <p className="text-sm text-red-600 leading-relaxed">
          {error}
        </p>
      )}
    </div>
  );
};
